import { useState, useEffect } from "react";
import { costsAPI } from "@/lib/api";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Settings as SettingsIcon,
  User,
  DollarSign,
  Percent,
  Save,
  Plus,
  Trash2,
  GripVertical,
} from "lucide-react";

const Settings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vatPercent, setVatPercent] = useState("22");
  const [costs, setCosts] = useState([]);

  useEffect(() => {
    loadCosts();
  }, []);

  const loadCosts = async () => {
    try {
      const res = await costsAPI.get();
      setCosts(res.data.costs || []);
      setVatPercent(res.data.vat_percent?.toString() || "22");
    } catch (error) {
      toast.error("Errore nel caricamento delle impostazioni");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCost = () => {
    const newCost = {
      id: crypto.randomUUID(),
      name: "",
      value: "",
      is_percentage: false,
    };
    setCosts([...costs, newCost]);
  };

  const handleRemoveCost = (id) => {
    setCosts(costs.filter((c) => c.id !== id));
  };

  const handleCostChange = (id, field, value) => {
    setCosts(
      costs.map((c) =>
        c.id === id ? { ...c, [field]: value } : c
      )
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validate costs
    const validCosts = costs.filter((c) => c.name.trim() !== "");
    if (validCosts.length === 0) {
      toast.error("Aggiungi almeno un costo");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        costs: validCosts.map((c) => ({
          id: c.id,
          name: c.name.trim(),
          value: parseFloat(c.value) || 0,
          is_percentage: c.is_percentage,
        })),
        vat_percent: parseFloat(vatPercent) || 22,
      };
      
      await costsAPI.update(payload);
      toast.success("Impostazioni salvate con successo");
      loadCosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#C9A227] animate-pulse">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">
          Impostazioni
        </h1>
        <p className="text-gray-500">
          Configura i costi fissi e il tuo profilo
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Card */}
        <Card className="bg-white/60 border-[#C9A227]/15">
          <CardHeader>
            <CardTitle className="text-lg font-serif text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-[#C9A227]" />
              Profilo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                <span className="text-2xl font-serif text-[#C9A227]">
                  {user?.name?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>
              <div>
                <p className="text-xl text-gray-900 font-medium">{user?.name}</p>
                <p className="text-gray-500">{user?.email}</p>
              </div>
            </div>

            <Separator className="bg-[#27272A] my-4" />

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">ID Utente</span>
                <span className="text-gray-700 font-mono text-xs">
                  {user?.id?.slice(0, 8)}...
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Registrato il</span>
                <span className="text-gray-700">
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString("it-IT")
                    : "-"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fixed Costs Card */}
        <Card className="bg-white/60 border-[#C9A227]/15">
          <CardHeader>
            <CardTitle className="text-lg font-serif text-gray-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#C9A227]" />
              Costi Fissi per Scorporo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              {/* VAT */}
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-1">
                  <Percent className="w-3 h-3" />
                  IVA (%)
                </Label>
                <Input
                  data-testid="settings-vat-input"
                  type="number"
                  step="0.1"
                  value={vatPercent}
                  onChange={(e) => setVatPercent(e.target.value)}
                  placeholder="22"
                  className="bg-gray-50 border-gray-200"
                />
              </div>

              <Separator className="bg-[#27272A] my-4" />

              {/* Dynamic Costs List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-700">Costi Personalizzati</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddCost}
                    data-testid="add-cost-btn"
                    className="text-[#C9A227] hover:bg-[#C9A227]/10"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Aggiungi
                  </Button>
                </div>

                {costs.length === 0 ? (
                  <div className="text-center py-6 text-gray-500 border border-dashed border-gray-200 rounded-lg">
                    <p className="text-sm">Nessun costo configurato</p>
                    <p className="text-xs mt-1">Clicca "Aggiungi" per iniziare</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {costs.map((cost, index) => (
                      <div
                        key={cost.id}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3"
                        data-testid={`cost-item-${index}`}
                      >
                        <div className="flex items-start gap-3">
                          <GripVertical className="w-4 h-4 text-gray-600 mt-3 flex-shrink-0" />
                          <div className="flex-1 space-y-3">
                            {/* Cost Name */}
                            <Input
                              data-testid={`cost-name-${index}`}
                              placeholder="Nome costo (es. Spedizione)"
                              value={cost.name}
                              onChange={(e) =>
                                handleCostChange(cost.id, "name", e.target.value)
                              }
                              className="bg-gray-50 border-gray-200"
                            />
                            
                            {/* Cost Value and Type */}
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <Input
                                  data-testid={`cost-value-${index}`}
                                  type="number"
                                  step="0.01"
                                  placeholder={cost.is_percentage ? "10" : "5.00"}
                                  value={cost.value}
                                  onChange={(e) =>
                                    handleCostChange(cost.id, "value", e.target.value)
                                  }
                                  className="bg-gray-50 border-gray-200"
                                />
                              </div>
                              
                              {/* Percentage Toggle */}
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">€</span>
                                <Switch
                                  checked={cost.is_percentage}
                                  onCheckedChange={(checked) =>
                                    handleCostChange(cost.id, "is_percentage", checked)
                                  }
                                  data-testid={`cost-percentage-${index}`}
                                />
                                <span className="text-xs text-gray-500">%</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Delete Button */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveCost(cost.id)}
                            data-testid={`remove-cost-${index}`}
                            className="text-gray-500 hover:text-red-400 hover:bg-red-400/10 mt-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Separator className="bg-[#27272A] my-4" />

              <Button
                type="submit"
                data-testid="save-settings-btn"
                disabled={saving}
                className="w-full bg-[#C9A227] text-black hover:bg-[#FFD700]"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Salvataggio..." : "Salva Impostazioni"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-white/60 border-[#C9A227]/15">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#C9A227]/10 rounded-lg">
              <SettingsIcon className="w-6 h-6 text-[#C9A227]" />
            </div>
            <div>
              <h3 className="text-gray-900 font-medium mb-2">
                Come funzionano i costi?
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Puoi aggiungere tutti i costi che vuoi con nome personalizzato. 
                Per ogni costo puoi scegliere se è un <strong className="text-gray-900">importo fisso (€)</strong> o 
                una <strong className="text-gray-900">percentuale (%)</strong> sul totale della vendita.
                <br /><br />
                <strong className="text-[#C9A227]">Esempi:</strong> Spedizione (€5), Commissione TikTok (10%), Packaging (€2), Gestione Ordini (3%)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
